// src/components/AGTFit/goals/Weight.js
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  FormControl, 
  Select, 
  MenuItem,
  CircularProgress,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { ChevronDown } from 'lucide-react';
import { format, startOfWeek, addDays, subDays, subMonths, subYears } from 'date-fns';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceDot
} from 'recharts';

// Color principal
const FIT_ORANGE = '#FF6B00';

// Colores para modo wallpaper
const WALLPAPER_COLORS = {
  bg: 'rgba(128, 128, 128, 0.3)',
  bgLight: 'rgba(128, 128, 128, 0.25)',
  border: 'rgba(128, 128, 128, 0.3)',
  text: '#FFFFFF',
  textSecondary: 'rgba(255, 255, 255, 0.7)'
};

// Nuevo NavigationBar estilizado con ancho adaptable
const StyledTabs = styled(Box)(({ theme }) => ({
  minHeight: '40px',
  height: '40px',
  background: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bg
    : (theme.palette.mode === 'dark'
      ? 'rgba(40, 40, 42, 0.94)'
      : 'rgba(242, 242, 247, 1)'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  padding: '6px',
  borderRadius: '24px',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  maxWidth: '350px',
  margin: '0 auto',
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(3),
  border: theme.palette.actualMode === 'wallpaper'
    ? `1px solid ${WALLPAPER_COLORS.border}`
    : 'none',
}));

// TabIndicator modificado para usar transform
const TabIndicator = styled('div')(({ theme }) => ({
  position: 'absolute',
  height: '28px',
  borderRadius: '24px',
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark'
      ? 'rgba(118, 118, 128, 0.24)'
      : '#FFFFFF'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  zIndex: 0,
  boxShadow:
    theme.palette.mode === 'dark'
      ? 'none'
      : '0 2px 8px rgba(0, 0, 0, 0.04)',
}));

// StyledTab con ancho adaptable al contenido
const StyledTab = styled(Box)(({ theme, selected }) => ({
  minHeight: '28px',
  height: '28px',
  minWidth: 'auto',
  padding: '0 16px',
  borderRadius: '24px',
  color:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.7)'
      : '#666666',
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'none',
  zIndex: 1,
  transition: 'all 0.2s',
  backgroundColor: 'transparent',
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  ...(selected && {
    backgroundImage: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
    backgroundClip: 'text',
    textFillColor: 'transparent',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  }),
  '&:hover': {
    backgroundImage: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
    backgroundClip: 'text',
    textFillColor: 'transparent',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundColor: 'transparent',
  },
}));

// Contenedor para gráfico de líneas - Aumentada la altura para mayor amplificación visual
const LineGraph = styled(Box)(({ theme }) => ({
  width: '100%',
  height: 300, // Aumentada de 220px a 300px para mayor amplificación visual
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bg
    : (theme.palette.mode === 'dark' ? 'rgba(30, 30, 32, 0.8)' : '#FFFFFF'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  borderRadius: 16,
  padding: theme.spacing(2, 2, 1, 2), // Reducir el padding inferior para alinear etiquetas
  marginBottom: theme.spacing(4), // Aumentar margen inferior para dar espacio a las etiquetas
  border: theme.palette.actualMode === 'wallpaper'
    ? `1px solid ${WALLPAPER_COLORS.border}`
    : `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)'}`,
  position: 'relative',
  boxShadow: theme.palette.actualMode === 'wallpaper'
    ? 'none'
    : (theme.palette.mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.2)' : '0 4px 12px rgba(0,0,0,0.05)'),
}));

// Tooltip personalizado para Recharts con fondo negro y texto con gradiente
const CustomTooltip = ({ active, payload, label }) => {
  const theme = useTheme();

  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.actualMode === 'wallpaper'
            ? WALLPAPER_COLORS.bg
            : 'rgba(0, 0, 0, 0.85)',
          backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
          WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
          border: theme.palette.actualMode === 'wallpaper'
            ? `1px solid ${WALLPAPER_COLORS.border}`
            : '1px solid rgba(0, 0, 0, 0.2)',
          padding: '8px 12px',
          borderRadius: '6px',
          boxShadow: theme.palette.actualMode === 'wallpaper'
            ? 'none'
            : '0 4px 12px rgba(0,0,0,0.2)',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: '#FFFFFF',
            fontWeight: 500,
            fontSize: '0.8rem',
          }}
        >
          {payload[0].payload.dayLabel}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            background: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textFillColor: 'transparent',
            fontWeight: 600,
            fontSize: '1rem',
          }}
        >
          {`${payload[0].value.toFixed(1)}`}
        </Typography>
      </Box>
    );
  }
  return null;
};

// Componente personalizado para renderizar valores sobre los puntos
const CustomizedLabel = (props) => {
  const { x, y, value, /* index, */ active } = props;
  
  // Solo mostrar etiquetas para valores válidos
  if (!value || value <= 0) return null;
  
  // Determinar si este punto es activo
  const isActive = active || props.payload?.active;
  
  return (
    <text
      x={x}
      y={y - 15} // Posicionarlo 15px arriba del punto
      fill="transparent"
      textAnchor="middle"
      dominantBaseline="middle"
      style={{
        fontWeight: isActive ? 600 : 400,
        fontSize: '0.7rem',
        background: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: isActive ? 'transparent' : '#666666',
      }}
    >
      {value.toFixed(1)}
    </text>
  );
};

// Componente de punto personalizado para Recharts
const CustomDot = (props) => {
  const { cx, cy, payload, value } = props;
 
  if (value <= 0) return null;
 
  const isActive = payload.active;
 
  return (
    <g>
      {/* Usar defs para definir el gradiente */}
      <defs>
        <linearGradient id="dotGradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FF9800" />
          <stop offset="100%" stopColor="#FF6B00" />
        </linearGradient>
      </defs>
      <circle
        cx={cx}
        cy={cy}
        r={6}
        fill={isActive ? "url(#dotGradient)" : 'rgba(150, 150, 150, 0.5)'}
        stroke={isActive ? 'rgba(255, 107, 0, 0.3)' : 'transparent'}
        strokeWidth={4}
        style={{
          transition: 'all 0.3s',
          cursor: 'pointer',
        }}
      />
    </g>
  );
};

const Weight = ({ weightData, loading, goalData, onPeriodChange }) => {
  const theme = useTheme();
  const [weightFilter, setWeightFilter] = useState('Weight');
  const [activeWeightTimeTab, setActiveWeightTimeTab] = useState(0);
  const [processedData, setProcessedData] = useState([]);
  
  // Referencias para los tabs
  const weightTabsContainerRef = useRef(null);
  const weightTabRefs = useRef([]);
  if (weightTabRefs.current.length === 0) {
    weightTabRefs.current = Array(6).fill(null).map(() => ({ current: null }));
  }
  
  // Estado para el indicador de tabs
  const [weightIndicatorStyle, setWeightIndicatorStyle] = useState({
    left: '6px',
    width: '74px'
  });

  // Opciones para los selectores
  const weightFilterOptions = [
    'Weight', 'Fat %', 'Waist', 'Neck', 'Hip', 'Chest', 'Arm', 'Thigh'
  ];
  
  // Opciones para los tabs de tiempo
  const timeTabOptions = ['Week', '7D', '1M', '3M', '1Y', 'MAX'];

  // Función para obtener el valor según el filtro seleccionado
  const getValueByFilter = useCallback((checkIn) => {
    if (!checkIn) return 0;

    // Añadir logging para depuración
    if (checkIn.measurements) {
    }

    switch (weightFilter) {
      case 'Weight':
        return parseFloat(checkIn.weight || 0);
      case 'Fat %':
        return parseFloat(checkIn.bodyFat || 0);
      case 'Waist':
        // Intentar diferentes formas de acceder al valor
        if (checkIn.measurements?.waist?.S) {
          return parseFloat(checkIn.measurements.waist.S || 0);
        } else if (typeof checkIn.measurements?.waist === 'string') {
          return parseFloat(checkIn.measurements.waist || 0);
        } else if (checkIn.measurements?.waist) {
          return parseFloat(checkIn.measurements.waist || 0);
        }
        return 0;
      case 'Neck':
        if (checkIn.measurements?.neck?.S) {
          return parseFloat(checkIn.measurements.neck.S || 0);
        } else if (typeof checkIn.measurements?.neck === 'string') {
          return parseFloat(checkIn.measurements.neck || 0);
        } else if (checkIn.measurements?.neck) {
          return parseFloat(checkIn.measurements.neck || 0);
        }
        return 0;
      case 'Hip':
        if (checkIn.measurements?.hip?.S) {
          return parseFloat(checkIn.measurements.hip.S || 0);
        } else if (typeof checkIn.measurements?.hip === 'string') {
          return parseFloat(checkIn.measurements.hip || 0);
        } else if (checkIn.measurements?.hip) {
          return parseFloat(checkIn.measurements.hip || 0);
        }
        return 0;
      case 'Chest':
        if (checkIn.measurements?.chest?.S) {
          return parseFloat(checkIn.measurements.chest.S || 0);
        } else if (typeof checkIn.measurements?.chest === 'string') {
          return parseFloat(checkIn.measurements.chest || 0);
        } else if (checkIn.measurements?.chest) {
          return parseFloat(checkIn.measurements.chest || 0);
        }
        return 0;
      case 'Arm':
        if (checkIn.measurements?.arm?.S) {
          return parseFloat(checkIn.measurements.arm.S || 0);
        } else if (typeof checkIn.measurements?.arm === 'string') {
          return parseFloat(checkIn.measurements.arm || 0);
        } else if (checkIn.measurements?.arm) {
          return parseFloat(checkIn.measurements.arm || 0);
        }
        return 0;
      case 'Thigh':
        if (checkIn.measurements?.thigh?.S) {
          return parseFloat(checkIn.measurements.thigh.S || 0);
        } else if (typeof checkIn.measurements?.thigh === 'string') {
          return parseFloat(checkIn.measurements.thigh || 0);
        } else if (checkIn.measurements?.thigh) {
          return parseFloat(checkIn.measurements.thigh || 0);
        }
        return 0;
      default:
        return parseFloat(checkIn.weight || 0);
    }
  }, [weightFilter]);

  // Función para obtener la unidad según el filtro seleccionado
  const getUnitByFilter = () => {
    switch (weightFilter) {
      case 'Weight':
        return 'kg';
      case 'Fat %':
        return '%';
      default:
        return 'cm';
    }
  };

  // Procesar datos según el período seleccionado y el filtro
  useEffect(() => {
    if (loading) return;

    // Crear un array de datos procesados según el período y filtro
    const today = new Date();
    let dataPoints = [];

    // Función de ayuda para depuración (Unused function)
    // const safeFormat = (dateValue, formatStr) => {
    //   try {
    //     if (!dateValue) return '';
    //     // Para strings de fecha, asegurarse de que tengan el formato correcto
    //     const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
    //     return format(date, formatStr);
    //   } catch (err) {
    //     console.error('Error formatting date:', dateValue, err);
    //     return '';
    //   }
    // };

    switch (activeWeightTimeTab) {
      case 0: // Week
        // Crear puntos para cada día de la semana (Lun-Dom)
        const startOfCurrentWeek = startOfWeek(today, { weekStartsOn: 1 }); // 1 = Lunes
        for (let i = 0; i < 7; i++) {
          const currentDate = addDays(startOfCurrentWeek, i);
          const dateStr = format(currentDate, 'yyyy-MM-dd');
          
          // Buscar si hay datos para este día específico
          const matchingData = Array.isArray(weightData) ? 
            weightData.find(item => {
              if (!item || !item.checkInDate) return false;
              
              try {
                // Convertir checkInDate a formato yyyy-MM-dd para comparar
                const itemDateStr = typeof item.checkInDate === 'string' 
                  ? item.checkInDate 
                  : format(new Date(item.checkInDate), 'yyyy-MM-dd');
                
                return itemDateStr === dateStr;
              } catch (err) {
                console.error('Error comparing dates:', item.checkInDate, err);
                return false;
              }
            }) 
            : null;
          
          const isToday = format(currentDate, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd');
          
          // Obtener valor según el filtro seleccionado
          let value = 0;
          if (matchingData) {
            value = getValueByFilter(matchingData);
          } else if (isToday && weightFilter === 'Weight' && goalData?.currentWeight) {
            value = goalData.currentWeight;
          } else if (i === 0 && weightFilter === 'Weight' && goalData?.initialWeight) {
            value = goalData.initialWeight;
          }
          
          dataPoints.push({
            date: dateStr,
            label: format(currentDate, 'EEE'), // Abreviatura del día (Mon, Tue, etc.)
            value: value,
            active: isToday,
            dayLabel: format(currentDate, 'EEE')
          });
        }
        break;
        
      case 1: // 7D - Últimos 7 días
        // Mostrar los últimos 7 días con el día actual a la derecha
        for (let i = 6; i >= 0; i--) {
          const currentDate = subDays(today, i);
          const dateStr = format(currentDate, 'yyyy-MM-dd');
          
          // Buscar si hay datos para este día específico
          const matchingData = Array.isArray(weightData) ? 
            weightData.find(item => {
              if (!item || !item.checkInDate) return false;
              
              try {
                // Convertir checkInDate a formato yyyy-MM-dd para comparar
                const itemDateStr = typeof item.checkInDate === 'string' 
                  ? item.checkInDate 
                  : format(new Date(item.checkInDate), 'yyyy-MM-dd');
                
                return itemDateStr === dateStr;
              } catch (err) {
                console.error('Error comparing dates:', item.checkInDate, err);
                return false;
              }
            }) : null;
          
          const isToday = i === 0;
          
          // Obtener valor según el filtro seleccionado
          let value = 0;
          if (matchingData) {
            value = getValueByFilter(matchingData);
          } else if (isToday && weightFilter === 'Weight' && goalData?.currentWeight) {
            value = goalData.currentWeight;
          }
          
          // Formatear la etiqueta según el día
          let label;
          if (i === 0) label = 'Today';
          else if (i === 1) label = 'Yday';
          else label = format(currentDate, 'dd/MM');
          
          dataPoints.push({
            date: dateStr,
            label: label,
            value: value,
            active: isToday,
            dayLabel: i === 0 ? 'Today' : i === 1 ? 'Yesterday' : format(currentDate, 'EEE')
          });
        }
        break;
        
      case 2: // 1M - Últimos 30 días
        // Mostrar los últimos 30 días agrupados por semanas (4 semanas)
        for (let i = 0; i < 4; i++) {
          // Calcular el inicio y fin de cada semana
          // La semana 4 es la semana actual, la semana 1 es de hace 3 semanas
          const weekStart = subDays(today, 28 - (i * 7)); // 28 días = 4 semanas
          const weekEnd = i === 3 ? today : subDays(today, 28 - ((i + 1) * 7) + 1);
          const weekLabel = `W${i + 1}`;
          
          // Buscar todos los datos en este rango de semana
          const weekData = Array.isArray(weightData) ? 
            weightData.filter(item => {
              if (!item || !item.checkInDate) return false;
              
              try {
                const itemDate = new Date(item.checkInDate);
                return itemDate >= weekStart && itemDate <= weekEnd;
              } catch (err) {
                console.error('Error filtering dates for week:', item.checkInDate, err);
                return false;
              }
            }) : [];
          
          // Calcular el valor promedio si hay datos
          let weekValue = 0;
          if (weekData.length > 0) {
            // Usar el campo correspondiente según el filtro para el cálculo
            const sum = weekData.reduce((acc, item) => {
              const value = getValueByFilter(item);
              return acc + (value || 0);
            }, 0);
            weekValue = sum / weekData.length;
            // Redondear a un decimal
            weekValue = Math.round(weekValue * 10) / 10;
          } else if (i === 3 && weightFilter === 'Weight' && goalData?.currentWeight) {
            // Si es la semana actual y no hay datos, usar el peso actual
            weekValue = goalData.currentWeight;
          }
          
          dataPoints.push({
            date: format(weekEnd, 'yyyy-MM-dd'),
            label: weekLabel,
            value: weekValue,
            active: i === 3, // La semana actual está activa
            dayLabel: weekLabel
          });
        }
        break;
        
      case 3: // 3M - Últimos 3 meses
        // Mostrar los últimos 3 meses
        for (let i = 2; i >= 0; i--) {
          const monthDate = subMonths(today, i);
          const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
          const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
          
          // Buscar todos los datos en este mes
          const monthData = Array.isArray(weightData) ? 
            weightData.filter(item => {
              if (!item || !item.checkInDate) return false;
              
              try {
                const itemDate = new Date(item.checkInDate);
                return itemDate >= monthStart && itemDate <= monthEnd;
              } catch (err) {
                console.error('Error filtering dates for month:', item.checkInDate, err);
                return false;
              }
            }) : [];
          
          // Calcular el valor promedio si hay datos
          let monthValue = 0;
          if (monthData.length > 0) {
            // Usar el campo correspondiente según el filtro para el cálculo
            const sum = monthData.reduce((acc, item) => {
              const value = getValueByFilter(item);
              return acc + (value || 0);
            }, 0);
            monthValue = sum / monthData.length;
            // Redondear a un decimal
            monthValue = Math.round(monthValue * 10) / 10;
          } else if (i === 0 && weightFilter === 'Weight' && goalData?.currentWeight) {
            // Si es el mes actual y no hay datos, usar el peso actual
            monthValue = goalData.currentWeight;
          }
          
          dataPoints.push({
            date: format(monthEnd, 'yyyy-MM-dd'),
            label: format(monthDate, 'MMM'),
            value: monthValue,
            active: i === 0, // El mes actual está activo
            dayLabel: format(monthDate, 'MMMM')
          });
        }
        break;
        
      case 4: // 1Y - Último año
        // Mostrar los últimos 12 meses
        for (let i = 11; i >= 0; i--) {
          const monthDate = subMonths(today, i);
          const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
          const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
          
          // Buscar todos los datos en este mes
          const monthData = Array.isArray(weightData) ? 
            weightData.filter(item => {
              if (!item || !item.checkInDate) return false;
              
              try {
                const itemDate = new Date(item.checkInDate);
                return itemDate >= monthStart && itemDate <= monthEnd;
              } catch (err) {
                console.error('Error filtering dates for month:', item.checkInDate, err);
                return false;
              }
            }) : [];
          
          // Calcular el valor promedio si hay datos
          let monthValue = 0;
          if (monthData.length > 0) {
            // Usar el campo correspondiente según el filtro para el cálculo
            const sum = monthData.reduce((acc, item) => {
              const value = getValueByFilter(item);
              return acc + (value || 0);
            }, 0);
            monthValue = sum / monthData.length;
            // Redondear a un decimal
            monthValue = Math.round(monthValue * 10) / 10;
          } else if (i === 0 && weightFilter === 'Weight' && goalData?.currentWeight) {
            // Si es el mes actual y no hay datos, usar el peso actual
            monthValue = goalData.currentWeight;
          }
          
          dataPoints.push({
            date: format(monthEnd, 'yyyy-MM-dd'),
            label: format(monthDate, 'MMM'),
            value: monthValue,
            active: i === 0, // El mes actual está activo
            dayLabel: format(monthDate, 'MMMM')
          });
        }
        break;
        
      case 5: // MAX - Todo el historial
        // Obtener el rango de fechas para todo el historial
        const earliestDate = goalData?.initialWeight ? subYears(today, 2) : subYears(today, 1);
        let currentDate = new Date(earliestDate.getFullYear(), earliestDate.getMonth(), 1);
        
        // Crear puntos para cada mes hasta la fecha actual
        while (currentDate <= today) {
          const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
          const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
          
          // Buscar todos los datos en este mes
          const monthData = Array.isArray(weightData) ? 
            weightData.filter(item => {
              if (!item || !item.checkInDate) return false;
              
              try {
                const itemDate = new Date(item.checkInDate);
                return itemDate >= monthStart && itemDate <= monthEnd;
              } catch (err) {
                console.error('Error filtering dates for month:', item.checkInDate, err);
                return false;
              }
            }) : [];
          
          // Calcular el valor promedio si hay datos
          let monthValue = 0;
          if (monthData.length > 0) {
            // Usar el campo correspondiente según el filtro para el cálculo
            const sum = monthData.reduce((acc, item) => {
              const value = getValueByFilter(item);
              return acc + (value || 0);
            }, 0);
            monthValue = sum / monthData.length;
            // Redondear a un decimal
            monthValue = Math.round(monthValue * 10) / 10; // CORREGIDO: weekValue -> monthValue
          } else if (
            currentDate.getMonth() === today.getMonth() && 
            currentDate.getFullYear() === today.getFullYear() && 
            weightFilter === 'Weight' &&
            goalData?.currentWeight
          ) {
            // Si es el mes actual y no hay datos, usar el peso actual
            monthValue = goalData.currentWeight;
          } else if (
            currentDate.getTime() === earliestDate.getTime() && 
            weightFilter === 'Weight' &&
            goalData?.initialWeight
          ) {
            // Si es el mes inicial y no hay datos, usar el peso inicial
            monthValue = goalData.initialWeight;
          }
          
          // Solo añadir puntos con valores o el mes actual/inicial
          if (monthValue > 0 || 
              (currentDate.getMonth() === today.getMonth() && currentDate.getFullYear() === today.getFullYear()) ||
              (currentDate.getTime() === earliestDate.getTime())
          ) {
            dataPoints.push({
              date: format(monthEnd, 'yyyy-MM-dd'),
              label: format(currentDate, 'MMM'),
              value: monthValue,
              active: currentDate.getMonth() === today.getMonth() && currentDate.getFullYear() === today.getFullYear(),
              dayLabel: format(currentDate, 'MMMM yyyy')
            });
          }
          
          // Avanzar al siguiente mes
          currentDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
        }
        break;
      default:
        // Default case - return empty data points
        break;
    }

    // Asegurar que haya al menos un punto para el valor actual
    if (dataPoints.length === 0 && weightFilter === 'Weight' && goalData?.currentWeight) {
      dataPoints.push({
        date: format(today, 'yyyy-MM-dd'),
        label: 'Today',
        value: goalData.currentWeight,
        active: true,
        dayLabel: 'Today'
      });
    }

    // Asegurar que al menos un punto tenga el valor inicial si está disponible
    const hasValues = dataPoints.some(point => point.value > 0);
    if (!hasValues && weightFilter === 'Weight' && goalData?.initialWeight) {
      // Usar el peso inicial para el primer punto
      if (dataPoints.length > 0) {
        dataPoints[0].value = goalData.initialWeight;
      }
    }

    // Siempre incluir el valor de hoy si está disponible
    const todayPoint = dataPoints.find(point => 
      point.date === format(today, 'yyyy-MM-dd')
    );
    
    if (todayPoint && weightFilter === 'Weight' && goalData?.currentWeight && todayPoint.value === 0) {
      todayPoint.value = goalData.currentWeight;
    }

    setProcessedData(dataPoints);
  }, [weightData, activeWeightTimeTab, loading, goalData, weightFilter, getValueByFilter]);

  // Actualizar la posición del indicador de tabs cuando cambia la pestaña activa
  useEffect(() => {
    const updateIndicator = () => {
      if (weightTabRefs.current[activeWeightTimeTab]?.current) {
        const tabElement = weightTabRefs.current[activeWeightTimeTab].current;
        const tabsContainer = weightTabsContainerRef.current;
        
        if (tabElement && tabsContainer) {
          // Obtener la posición del contenedor de pestañas
          const containerRect = tabsContainer.getBoundingClientRect();
          // Obtener la posición de la pestaña activa
          const tabRect = tabElement.getBoundingClientRect();
          
          // Calcular la posición relativa de la pestaña dentro del contenedor
          const left = tabRect.left - containerRect.left;
          
          setWeightIndicatorStyle({
            left: `${left}px`,
            width: `${tabRect.width}px`
          });
        }
      }
    };
    
    // Pequeño retraso para asegurar que los elementos estén renderizados
    const timer = setTimeout(updateIndicator, 50);
    return () => clearTimeout(timer);
  }, [activeWeightTimeTab, weightTabRefs]);

  // Manejadores para los cambios de pestaña
  const handleWeightTimeTabChange = (newValue) => {
    setActiveWeightTimeTab(newValue);
    // Cargar datos según el período de tiempo
    const periods = ['week', 'week', 'month', '3month', 'year', 'max'][newValue];
    onPeriodChange(periods);
  };

  // Calcular el promedio semanal según el filtro seleccionado
  const calculateWeekAverage = () => {
    if (!weightData || weightData.length === 0) {
      return weightFilter === 'Weight' ? (goalData?.currentWeight || 0) : 0;
    }
  
    const today = new Date();
    const weekStart = subDays(today, 7);
  
    // Filtrar datos de la última semana
    const weekData = Array.isArray(weightData) ? 
      weightData.filter(item => {
        if (!item || !item.checkInDate) return false;
  
        try {
          const itemDate = new Date(item.checkInDate);
          return itemDate >= weekStart && itemDate <= today;
        } catch (err) {
          console.error('Error filtering dates for week average:', item.checkInDate, err);
          return false;
        }
      }) : [];
  
    if (weekData.length === 0) {
      return weightFilter === 'Weight' ? (goalData?.currentWeight || 0) : 0;
    }
  
    // Calcular promedio según el filtro seleccionado
    let sum = 0;
    let count = 0;
  
    weekData.forEach(item => {
      const value = getValueByFilter(item);
      if (value > 0) {
        sum += value;
        count++;
      }
    });
  
    return count > 0 ? (sum / count).toFixed(1) : 0;
  };

  // Renderizar el gráfico de líneas usando Recharts - FUNCIÓN ACTUALIZADA
  const renderWeightChart = (data) => {
    // const hasRealData = data.some(item => item.value > 0); // Unused variable

    // Calcular valores mínimos y máximos para el dominio del eje Y
    const validValues = data.filter(d => d.value > 0).map(d => d.value);
    const avg = validValues.length > 0 ?
      validValues.reduce((a, b) => a + b, 0) / validValues.length : 0;

    // Encuentra el valor mínimo y máximo de los datos válidos
    const minDataValue = Math.min(...validValues);
    const maxDataValue = Math.max(...validValues);

    // Calcula el rango actual de los datos
    // const dataRange = maxDataValue - minDataValue; // Unused variable
    
    // Define un rango más estrecho para amplificar visualmente las diferencias
    let minValue, maxValue;
    
    // Ajusta este valor para controlar la amplificación de diferencias
    // Valores más pequeños = mayor amplificación de diferencias
    const rangeMultiplier = 0.015; // 1.5% alrededor del promedio
    
    minValue = avg - (avg * rangeMultiplier);
    maxValue = avg + (avg * rangeMultiplier);
    
    // Asegúrate de que todos los puntos de datos estén dentro del rango
    if (minDataValue < minValue) minValue = minDataValue * 0.99;
    if (maxDataValue > maxValue) maxValue = maxDataValue * 1.01;
    
    return (
      <LineGraph>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 20, right: 10, left: 40, bottom: 0 }} // Aumenta margen izquierdo para eje Y
          >
            <defs>
              <linearGradient id="orangeGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#FF9800" />
                <stop offset="100%" stopColor="#FF6B00" />
              </linearGradient>
            </defs>
            
            {/* Añadir cuadrícula para mejor referencia */}
            <CartesianGrid 
              strokeDasharray="3 3" 
              vertical={false} 
              stroke={theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'} 
            />
            
            {/* Línea de puntos para conectar los valores */}
            <Line
              type="monotone"
              dataKey="value"
              stroke={theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'}
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={<CustomDot />}
              activeDot={{ 
                r: 8, 
                fill: "url(#orangeGradient)", 
                stroke: 'rgba(255, 107, 0, 0.3)', 
                strokeWidth: 4 
              }}
              isAnimationActive={false}
              label={<CustomizedLabel />}
            />
            
            {/* Eje X con las etiquetas de fecha movidas más abajo */}
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              tick={(props) => {
                const { x, y, payload } = props;
                // Encontrar el punto de datos correspondiente a esta etiqueta
                const dataPoint = data.find(d => d.label === payload.value);
            
                // Determinar el estilo según si es activo, tiene valor, etc.
                let textColor;
                let fontWeight = 400;
            
                if (dataPoint) {
                  if (dataPoint.active) {
                    // Punto activo (día actual) - usar gradiente naranja
                    textColor = "url(#orangeGradient)";
                    fontWeight = 600;
                  } else if (dataPoint.value > 0) {
                    // Punto con valor - usar color de texto primario
                    textColor = theme.palette.text.primary;
                    fontWeight = 500;
                  } else {
                    // Punto sin valor - usar color de texto secundario
                    textColor = theme.palette.text.secondary;
                  }
                } else {
                  textColor = theme.palette.text.secondary;
                }
            
                return (
                  <g transform={`translate(${x},${y + 20})`}>
                    <text 
                      x={0} 
                      y={0} 
                      textAnchor="middle" 
                      fill={textColor}
                      style={{ 
                        fontSize: '12px',
                        fontWeight: fontWeight,
                      }}
                    >
                      {payload.value}
                    </text>
                  </g>
                );
              }}
              dy={15}
              height={40}
            />
            
            {/* Eje Y ahora visible con dominio fijo */}
            <YAxis 
              domain={[minValue, maxValue]}
              allowDataOverflow={true}
              tickCount={4} // Cambiado de 5 a 3 para mostrar solo 3 valores
              axisLine={false}
              tickLine={false}
              tickFormatter={(value) => Math.round(value)} // Formatea como entero
              tick={(props) => {
                const { x, y, payload } = props;
                return (
                  <g transform={`translate(${x},${y})`}>
                    <text 
                      x={0} 
                      y={0} 
                      textAnchor="end" 
                      fill={theme.palette.text.secondary}
                      style={{ 
                        fontSize: '12px',
                      }}
                    >
                      {Math.round(payload.value)}
                    </text>
                  </g>
                );
              }}
              width={30}
            />
            
            {/* Tooltip personalizado */}
            <Tooltip 
              content={<CustomTooltip />} 
              cursor={false}
            />
            
            {/* Puntos de referencia para los valores activos */}
            {data.map((point, index) => (
              point.active && point.value > 0 ? (
                <ReferenceDot
                  key={`ref-dot-${index}`}
                  x={point.label}
                  y={point.value}
                  r={6}
                  fill={FIT_ORANGE}
                  stroke="rgba(255, 107, 0, 0.3)"
                  strokeWidth={4}
                />
              ) : null
            ))}
          </LineChart>
        </ResponsiveContainer>
      </LineGraph>
    );
  };

  // Manejar cambio de filtro
  const handleFilterChange = (event) => {
    setWeightFilter(event.target.value);
  };

  return (
    <Box sx={{ mb: 5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>
          {weightFilter}
        </Typography>
        
        <FormControl variant="standard" size="small" sx={{ minWidth: 120 }}>
          <Select
            value={weightFilter}
            onChange={handleFilterChange}
            disableUnderline
            sx={{ 
              fontSize: '0.875rem',
              '& .MuiSelect-select': {
                paddingBottom: 0
              }
            }}
            IconComponent={ChevronDown}
          >
            {weightFilterOptions.map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      {/* Current y Week Average */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Current
          </Typography>
          <Typography variant="subtitle1" fontWeight={600}>
            {weightFilter === 'Weight' && goalData?.currentWeight ? 
              `${goalData.currentWeight} ${getUnitByFilter()}` : 
              'No data'}
          </Typography>
        </Box>
        
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="body2" color="text.secondary">
            Week Average
          </Typography>
          <Typography variant="subtitle1" fontWeight={600}>
            {`${calculateWeekAverage()} ${getUnitByFilter()}`}
          </Typography>
        </Box>
      </Box>
      
      {/* Navigation Bar para el tiempo */}
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mb: 4 }}>
        <StyledTabs ref={weightTabsContainerRef}>
          <TabIndicator style={weightIndicatorStyle} />
          {timeTabOptions.map((tab, index) => (
            <StyledTab 
              key={tab}
              ref={weightTabRefs[index]}
              onClick={() => handleWeightTimeTabChange(index)}
              selected={activeWeightTimeTab === index}
            >
              {tab}
            </StyledTab>
          ))}
        </StyledTabs>
      </Box>
      
      {/* Gráfico de peso con Recharts */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress size={30} sx={{ color: FIT_ORANGE }} />
        </Box>
      ) : (
        renderWeightChart(processedData)
      )}
    </Box>
  );
};

export default Weight;